
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { LoginForm } from "@/components/auth/login-form";
import { supabase } from "@/integrations/supabase/client";
import { sanitizeRedirect } from "@/utils/auth-redirect";
import { toast } from "sonner";
import { claimPlayer } from "@/utils/claim-player";
import { hasActiveCCTeamRole } from "@/hooks/use-cc-team-roles";
import { bindPendingGuardianInvites } from "@/utils/guardian-binding";


const withTimeout = <T,>(p: Promise<T>, ms: number, fallback: T): Promise<T> =>
  Promise.race([
    p,
    new Promise<T>((resolve) => setTimeout(() => resolve(fallback), ms)),
  ]);

const Login = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(true);
  const [redirecting, setRedirecting] = useState(false);

  const context = searchParams.get("context") as "parent" | "coach" | null;
  const redirectParam = sanitizeRedirect(searchParams.get("redirect"));

  const processClaimIntent = async () => {
    const raw = sessionStorage.getItem("claimIntent");
    if (!raw) return null;

    try {
      const intent = JSON.parse(raw);
      sessionStorage.removeItem("claimIntent");
      const result = await claimPlayer(intent.playerId, intent.teamId, "your child");
      if (result.success) {
        toast.success(result.message);
      } else if (!result.needsAuth) {
        toast.error(result.message);
      }
      try {
        const url = new URL(intent.returnUrl || "", window.location.origin);
        return url.pathname + url.search + url.hash;
      } catch {
        return intent.returnUrl || null;
      }
    } catch {
      sessionStorage.removeItem("claimIntent");
      return null;
    }
  };

  const handlePostAuthRedirect = async (userId?: string) => {
    // Attach any co-parent invitations sent to this email before the redirect
    // decision, so an invited-only parent is routed to /parent.
    await withTimeout(bindPendingGuardianInvites(), 2500, 0);

    const claimReturnUrl = await processClaimIntent();


    if (claimReturnUrl) {
      navigate(claimReturnUrl, { replace: true });
      return;
    }

    if (redirectParam) {
      navigate(redirectParam, { replace: true });
      return;
    }

    // Check if user is a guardian (parent-only) — route to /parent instead of /teams.
    // Each lookup is time-boxed so a hung network call (e.g. preview fetch proxy)
    // never blocks the redirect.
    try {
      if (userId) {
        const guardianQuery: Promise<{ id: string }[]> = Promise.resolve(
          supabase
            .from("gameon_parent_guardians")
            .select("id")
            .eq("user_id", userId)
            .in("status", ["active", "pending"])
            .limit(1)
        ).then((r) => (r.data ?? []) as { id: string }[]);

        const teamQuery: Promise<{ id: string }[]> = Promise.resolve(
          supabase
            .from("teamvote_teams")
            .select("id")
            .eq("coach_id", userId)
            .limit(1)
        ).then((r) => (r.data ?? []) as { id: string }[]);

        const [guardianRows, teamRows] = await Promise.all([
          withTimeout(guardianQuery, 2000, [] as { id: string }[]),
          withTimeout(teamQuery, 2000, [] as { id: string }[]),
        ]);

        if (guardianRows.length > 0 && teamRows.length === 0) {
          const hasCCRole = await withTimeout(
            hasActiveCCTeamRole(userId),
            2000,
            false
          );
          if (!hasCCRole) {
            navigate("/parent", { replace: true });
            return;
          }
        }
      }
    } catch (err) {
      console.error("Error checking user role:", err);
    }

    navigate("/teams", { replace: true });
  };

  useEffect(() => {
    const checkSession = async () => {
      if (redirecting) return;

      try {
        const sessionResult = await withTimeout(
          supabase.auth.getSession(),
          3000,
          null as Awaited<ReturnType<typeof supabase.auth.getSession>> | null
        );
        const session = sessionResult?.data?.session ?? null;

        if (session?.access_token && session?.refresh_token) {
          console.log("Valid session found, redirecting");
          setRedirecting(true);
          await handlePostAuthRedirect(session.user.id);
          return;
        }

        setIsLoading(false);
      } catch (error) {
        console.error("Error checking session:", error);
        setIsLoading(false);
      }
    };

    checkSession();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        console.log("Auth state changed in Login page:", event);

        if (session && (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED')) {
          setRedirecting(true);
          await handlePostAuthRedirect(session.user.id);
        }
      }
    );

    return () => {
      subscription.unsubscribe();
    };
  }, [navigate, redirecting]);

  const handleLoginSuccess = () => {
    console.log("Login form submitted successfully");
    setRedirecting(true);
  };

  if (isLoading) {
    return (
      <div className="max-container my-8 flex items-center justify-center min-h-[50vh]">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="max-container my-8">
      <div className="max-w-xl mx-auto">
        <LoginForm onSuccess={handleLoginSuccess} context={context || undefined} />
      </div>
    </div>
  );
};

export default Login;
